import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminButton, AdminFormField, AdminTextInput } from "@/components/admin";
import { clearSession, fetchMe, login } from "../../../../lib/api";

const KR_FONT = { fontFamily: '"Noto Sans KR", sans-serif' };

const AdminLoginForm = () => {
  const navigate = useNavigate();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleLogin = async () => {
    if (!loginId.trim() || !password.trim()) {
      setErrorMsg("아이디와 비밀번호를 모두 입력해 주세요.");
      return;
    }

    setErrorMsg("");
    setIsLoading(true);

    try {
      await login(loginId.trim(), password);
      const me = await fetchMe();

      if (!["ADMIN", "ROLE_ADMIN"].includes(me.roleType)) {
        clearSession();
        setErrorMsg("관리자 권한이 있는 계정만 접속할 수 있습니다.");
        return;
      }

      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      clearSession();
      setErrorMsg(error instanceof Error ? error.message : "로그인 처리 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      void handleLogin();
    }
  };

  return (
    <div className="flex flex-col gap-5 px-8 pb-10">
      <p
        className="text-xs font-semibold text-slate-500 tracking-widest uppercase"
        style={{ ...KR_FONT, letterSpacing: "0.12em" }}
      >
        관리자 로그인
      </p>

      <AdminFormField label="아이디" htmlFor="admin-login-id" className="space-y-2" labelClassName="pl-1 font-semibold text-slate-400" style={KR_FONT}>
        <div
          className="flex items-center gap-3 rounded-lg px-4 transition-all"
          style={{
            height: "3rem",
            background: "#1E293B",
            border: "1px solid #334155",
          }}
        >
          <div className="flex items-center justify-center w-5 h-5 shrink-0">
            <i className="ri-user-line text-slate-500 text-base" />
          </div>
          <AdminTextInput
            id="admin-login-id"
            type="text"
            placeholder="아이디를 입력하세요"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="bare"
            className="flex-1 text-sm text-slate-200 placeholder-slate-600 bg-transparent outline-none border-none"
            style={KR_FONT}
            autoComplete="username"
          />
        </div>
      </AdminFormField>

      <AdminFormField label="비밀번호" htmlFor="admin-login-password" className="space-y-2" labelClassName="pl-1 font-semibold text-slate-400" style={KR_FONT}>
        <div
          className="flex items-center gap-3 rounded-lg px-4 transition-all"
          style={{
            height: "3rem",
            background: "#1E293B",
            border: "1px solid #334155",
          }}
        >
          <div className="flex items-center justify-center w-5 h-5 shrink-0">
            <i className="ri-lock-password-line text-slate-500 text-base" />
          </div>
          <AdminTextInput
            id="admin-login-password"
            type={showPassword ? "text" : "password"}
            placeholder="비밀번호를 입력하세요"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="bare"
            className="flex-1 text-sm text-slate-200 placeholder-slate-600 bg-transparent outline-none border-none"
            style={KR_FONT}
            autoComplete="current-password"
          />
          <AdminButton variant="plain" size="icon"
            onClick={() => setShowPassword((value) => !value)}
            className="shrink-0 text-slate-500 hover:text-slate-300"
            aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
          >
            <i
              className={`text-slate-500 text-sm hover:text-slate-300 transition-colors ${
                showPassword ? "ri-eye-off-line" : "ri-eye-line"
              }`}
            />
          </AdminButton>
        </div>
      </AdminFormField>

      {errorMsg !== "" && (
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-lg"
          style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)" }}
        >
          <div className="flex items-center justify-center w-4 h-4 shrink-0">
            <i className="ri-error-warning-line text-red-400 text-sm" />
          </div>
          <p className="text-xs text-red-400" style={KR_FONT}>
            {errorMsg}
          </p>
        </div>
      )}

      <AdminButton
        variant="primary"
        size="auto"
        onClick={() => void handleLogin()}
        disabled={isLoading}
        className="w-full rounded-lg text-white text-sm font-semibold tracking-wide transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 mt-1"
        style={{
          ...KR_FONT,
          background: "linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)",
          height: "3rem",
          boxShadow: "0 4px 16px rgba(59,130,246,0.3)",
        }}
      >
        {isLoading ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base" />
            <span>인증 중...</span>
          </>
        ) : (
          <>
            <i className="ri-login-box-line text-base" />
            <span>로그인</span>
          </>
        )}
      </AdminButton>

      <div className="flex items-center justify-center gap-2 mt-1">
        <div className="flex items-center justify-center w-4 h-4">
          <i className="ri-shield-check-line text-slate-600 text-xs" />
        </div>
        <p className="text-xs text-slate-600" style={KR_FONT}>
          권한이 있는 관리자만 접근할 수 있습니다
        </p>
      </div>
    </div>
  );
};

export default AdminLoginForm;
